import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { searchMedicationInstructions, searchMedications } from "../../api/services/clinic/medications";
import { getMedicationDetail } from "../../api/services/clinic/treatment";
import Button from "../../components/Button";
import InputField from "../../components/InputField";
import { useToast } from "../../contexts/ToastContext";
import { ROUND_UP, roundToInt } from "../../utils/rounding";

export interface MedicationUnit {
    unit_name: string;
    is_default?: boolean;
    min_dispense_quantity?: number;
    stock_quantity?: number | null;
    price?: number | null;
}

export interface MedicationDetail {
    id: number;
    name: string;
    units: MedicationUnit[];
    usage_instructions?: string | null;
}

export interface MedicationFormValue {
    action?: string;
    id?: number;
    name?: string;
    medication?: MedicationDetail | null;
    medication_id?: number | null;
    medicine_id?: number | null;
    quantity: number | string;
    unit: string;
    usage_instructions?: string;
    usage_instruction?: string;
    note?: string;
    price?: number | string | null;
    units?: MedicationUnit[];
    [key: string]: unknown;
}

interface MedicationItemFormProps {
    formValue: MedicationFormValue;
    onChange: (value: MedicationFormValue, options?: { triggerSave?: boolean }) => void;
    onDelete?: () => void;
    itemNumber?: number;
    errorText?: string;
}

interface SearchOptions {
    page?: number;
}

interface SearchResponse {
    results?: Array<Record<string, unknown>>;
    total_pages?: number | null;
}

interface SearchResult {
    results: [Record<string, unknown>, string][];
    hasMore: boolean;
}

interface SuggestionTarget<T> extends EventTarget {
    value: string;
    selectedSuggestionValue?: T | null;
}

type MedicationSuggestionChangeEvent = ChangeEvent<HTMLInputElement> & { target: SuggestionTarget<MedicationDetail> };
type UsageSuggestion = { usage_instructions?: string; code?: string };
type UsageSuggestionChangeEvent = ChangeEvent<HTMLInputElement> & { target: SuggestionTarget<UsageSuggestion> };

/**
 * MedicationItemForm
 * 
 * A form component for entering medication item details.
 * 
 * @param {object} props
 * @param {object} props.formValue
 *   The current form values for the medication item.
 * @param {function} props.onChange
 *  Function to call when form values change.
 * @param {function} props.onDelete
 *  Function to call when the item is to be deleted.
 * @param {number} [props.itemNumber=1]
 *  The item number to display in the form.
 * @param {string} [props.errorText]
 *  Error text to display for the form.
 */
export default function MedicationItemForm({
    formValue,
    onChange,
    onDelete,
    itemNumber = 1,
    errorText,
}: MedicationItemFormProps) {
    const { showErrorToast } = useToast();
    const [searchValue, setSearchValue] = useState(formValue?.name || "");
    const [medicationDetail, setMedicationDetail] = useState<MedicationDetail | null>(formValue?.medication ?? null);
    const medicationDetailRequestRef = useRef(0);

    useEffect(() => {
        if (formValue?.medication) {
            setMedicationDetail(formValue.medication);
            return;
        }

        const medicationId = formValue?.medicine_id ?? formValue?.medication_id;
        if (!medicationId) {
            setMedicationDetail(null);
            return;
        }

        const requestId = medicationDetailRequestRef.current + 1;
        medicationDetailRequestRef.current = requestId;
        let isActive = true;

        (async () => {
            try {
                const detail = await getMedicationDetail(medicationId);
                if (!isActive || medicationDetailRequestRef.current !== requestId) return;
                setMedicationDetail((detail as MedicationDetail | null) || null);
            } catch (err) {
                if (!isActive || medicationDetailRequestRef.current !== requestId) return;
                console.error(err);
                showErrorToast("ไม่สามารถโหลดรายละเอียดรายการยา");
            }
        })();

        return () => {
            isActive = false;
        };
    }, [formValue?.medication, formValue?.medicine_id, formValue?.medication_id, showErrorToast]);

    const activeMedication = formValue.medication || medicationDetail;

    useEffect(() => {
        if (formValue?.name) {
            setSearchValue(formValue.name);
            return;
        }
        if (activeMedication?.name) {
            setSearchValue(activeMedication.name);
            return;
        }
        setSearchValue("");
    }, [formValue?.name, activeMedication?.name]);

    const defaultUnitInfo = (medication: MedicationDetail | null | undefined) => {
        return medication && medication.units.find((unit) => unit.is_default);
    };

    const defaultUnitName = (medication: MedicationDetail | null | undefined): string => {
        return (defaultUnitInfo(medication)?.unit_name) || "";
    };

    const resolveMinDispenseQuantity = (medication: MedicationDetail | null | undefined, unitName: string): number => {
        const resolvedUnit = medication?.units?.find((unit) => unit.unit_name === unitName)
            || medication?.units?.find((unit) => unit.is_default);
        const minQuantity = Number(resolvedUnit?.min_dispense_quantity);
        if (Number.isFinite(minQuantity) && minQuantity > 0) {
            return minQuantity;
        }
        return 1;
    };

    const stockQuantity: number | null = (() => {
        const medication = activeMedication;
        const unit = formValue.unit;

        if (medication && medication.units) {
            const unitInfo = medication.units.find((u) => u.unit_name === unit);
            return unitInfo?.stock_quantity == null ? null : Number(unitInfo.stock_quantity);
        }
        return null;
    })();

    async function onSearchMedicine(query: string, { page = 1 }: SearchOptions = {}): Promise<SearchResult> {
        try {
            const response = await searchMedications(query, { page, pageSize: 10 }) as SearchResponse;
            const results = Array.isArray(response?.results) ? response.results : [];
            const totalPages = Number.parseInt(String(response?.total_pages ?? 1), 10);
            return {
                results: results.map((item) => [item, String(item.name || "")]),
                hasMore: page < totalPages,
            };
        } catch (err) {
            return { results: [], hasMore: false };
        }
    }

    async function onSearchMedicationUsage(query: string, { page = 1 }: SearchOptions = {}): Promise<SearchResult> {
        try {
            const response = await searchMedicationInstructions(query, { page, pageSize: 5 }) as SearchResponse;
            const results = Array.isArray(response?.results) ? response.results : [];
            const totalPages = Number.parseInt(String(response?.total_pages ?? 1), 10);
            return {
                results: results.map((item) => [item, String(item.usage_instructions || item.code || "")]),
                hasMore: page < totalPages,
            };
        } catch (err) {
            showErrorToast("ไม่สามารถค้นหารายละเอียดการใช้ยา", err);
            return { results: [], hasMore: false };
        }
    }

    function calculateTotalPrice(medication: MedicationDetail | null | undefined, quantity: number | string, unit: string): string | null {
        const quantityNumber = Number(quantity);
        if (!medication?.units || !Number.isFinite(quantityNumber) || quantityNumber <= 0) {
            return null;
        }

        const unitPrice = Number(
            medication.units.find((item) => item.unit_name === unit)?.price ?? 0
        );
        return roundToInt(unitPrice * quantityNumber, ROUND_UP).toString();
    }

    function handleMedicationQuantityChange(event: ChangeEvent<HTMLInputElement>) {
        const value = event.target.value;
        const intValue = value.replace(/[^0-9]/g, '');

        if (intValue === "0") {
            showErrorToast("จำนวนยาต้องมากกว่า 0");
            return;
        }

        if (stockQuantity !== null && Number(intValue) > stockQuantity) {
            showErrorToast("ไม่สามารถสั่งเกินจำนวนคงเหลือได้");
            return;
        }

        const totalPrice = calculateTotalPrice(activeMedication, intValue, formValue.unit);
        onChange({ ...formValue, quantity: intValue, price: totalPrice });
    }

    function handleUnitChange(event: ChangeEvent<HTMLSelectElement>) {
        const nextUnit = event.target.value;
        const resolvedMedication = activeMedication || formValue.medication || null;
        const resolvedMedicationId =
            resolvedMedication?.id ?? formValue.medication_id ?? formValue.medicine_id ?? null;
        const totalPrice = calculateTotalPrice(resolvedMedication, formValue.quantity, nextUnit);
        const nextUnitInfo = resolvedMedication?.units?.find((u) => u.unit_name === nextUnit);
        const nextQuantity = resolvedMedication
            && Number(nextUnitInfo?.stock_quantity ?? 0) > 0
            ? resolveMinDispenseQuantity(resolvedMedication, nextUnit)
            : 0;

        onChange({
            ...formValue,
            medication: resolvedMedication,
            medication_id: resolvedMedicationId,
            quantity: nextQuantity,
            unit: nextUnit,
            price: totalPrice ?? "",
        });
    }

    function buildDeleteButton() {
        return (<Button hierarchy="danger" size="md" icon="trash" onClick={onDelete} />)
    }

    const resolvedMedicationForPrice = activeMedication || formValue.medication || null;
    const calculatedPrice = calculateTotalPrice(
        resolvedMedicationForPrice,
        formValue.quantity,
        formValue.unit
    );

    return (
        <div className="flex flex-col gap-3">
            <div className="flex gap-3 items-start">
                <div className="flex-1">
                    <InputField
                        name="medication"
                        label="ชื่อยาและเวชภัณฑ์"
                        placeholder="ระบุยา..."
                        type="search"
                        allowFreeText={false}
                        onSearch={onSearchMedicine}
                        value={searchValue}
                        onChange={async (event: MedicationSuggestionChangeEvent) => {
                            const medication = event.target.selectedSuggestionValue;
                            const inputValue = event.target.value;
                            if (!medication) {
                                setSearchValue(inputValue);
                                if (inputValue === "") {
                                    setMedicationDetail(null);
                                    onChange({
                                        ...formValue,
                                        medication: null,
                                        medication_id: null,
                                        medicine_id: null,
                                        name: "",
                                        unit: "",
                                        quantity: "",
                                        price: "",
                                    }, { triggerSave: false });
                                }
                                return;
                            }

                            setSearchValue(inputValue);
                            let medicationDetail = medication;

                            if (medication?.id) {
                                try {
                                    medicationDetail = await getMedicationDetail(medication.id) as MedicationDetail;
                                } catch (err) {
                                    console.error(err);
                                    showErrorToast("ไม่สามารถโหลดรายละเอียดรายการยา");
                                }
                            }

                            const unit = defaultUnitName(medicationDetail);
                            const unitInfo = defaultUnitInfo(medicationDetail);
                            const quantity = medicationDetail && Number(unitInfo?.stock_quantity ?? 0) > 0
                                ? resolveMinDispenseQuantity(medicationDetail, unit)
                                : 0;

                            setMedicationDetail(medicationDetail || null);
                            onChange({
                                ...formValue,
                                medication: medicationDetail,
                                medication_id: medicationDetail ? medicationDetail.id : null,
                                medicine_id: medicationDetail ? medicationDetail.id : null,
                                name: inputValue,
                                unit: unit,
                                quantity: quantity,
                                price: medicationDetail ? calculateTotalPrice(medicationDetail, quantity, unit) : "",
                                usage_instructions: medicationDetail
                                    ? (medicationDetail.usage_instructions || "")
                                    : formValue.usage_instructions,
                            }, { triggerSave: true });
                        }}
                        errorText={errorText}
                    />
                </div>
                <InputField
                    name="quantity"
                    label="จำนวน"
                    className="w-[80px]"
                    type="number"
                    inputMode="numeric"
                    successText={stockQuantity && stockQuantity > 0 ? `คงเหลือ ${stockQuantity}` : ""}
                    errorText={stockQuantity === 0 ? `คงเหลือ ${stockQuantity}` : ""}
                    value={formValue.quantity || ""}
                    onChange={handleMedicationQuantityChange}
                    disabled={formValue.quantity === 0}
                />
                <InputField
                    name="unit"
                    label="หน่วย"
                    className="w-[110px]"
                    type="select"
                    choices={formValue.units && formValue.units.length > 0 ? formValue.units.map((unit) => [unit.unit_name, unit.unit_name]) : activeMedication && activeMedication.units.map((unit) => [unit.unit_name, unit.unit_name]) || []}
                    value={formValue.unit || defaultUnitName(activeMedication)}
                    onChange={handleUnitChange}
                />
                <InputField
                    name="price"
                    label="ราคารวม"
                    className="w-[100px]"
                    type="number"
                    value={calculatedPrice ?? ""}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ ...formValue, price: e.target.value })}
                    disabled
                />
                {onDelete && (<div className="pt-7">{buildDeleteButton()}</div>)}
            </div>
            <InputField
                name="usage_instructions"
                label="วิธีใช้"
                placeholder="เช่น po pn q 4-5w..."
                type="search"
                onSearch={onSearchMedicationUsage}
                value={formValue.usage_instructions || ""}
                onChange={(event: UsageSuggestionChangeEvent) => {
                    const usage = event.target.selectedSuggestionValue;
                    onChange({
                        ...formValue,
                        usage_instructions: usage ? (usage.usage_instructions || usage.code) : event.target.value,
                    });
                }}
                className="flex-1"
            />
        </div>
    );
}
